import React, { useState, useEffect } from 'react'
import { hsclLogo2, agoraCoreNew } from '../../assets/images'
import { Link } from 'react-router-dom'
import { toast } from 'react-hot-toast'
import { useForgotPassword } from '../../hooks/usePasswordReset'
import {
    IconUsersGroup,
    IconClock,
    IconChartBar,
    IconCreditCard,
    IconChevronLeft
} from '@tabler/icons-react'

export default function ForgotPassword() {
    const [email, setEmail] = useState('')
    const [submitted, setSubmitted] = useState(false)
    const [isLoaded, setIsLoaded] = useState(false)
    const { mutate: forgotPassword, isPending: loading } = useForgotPassword()

    useEffect(() => {
        setIsLoaded(true)
    }, [])

    const handleSubmit = (e) => {
        e.preventDefault()
        if (!email) return
        forgotPassword({ email }, {
            onSuccess: (data) => {
                setSubmitted(true)
                toast.success(data.message)
            },
        })
    }

    return (
        <div className='flex h-svh overflow-hidden bg-white'>
            {/* Left Side: Brand & Features (40%) */}
            <div className='hidden lg:flex lg:w-[40%] flex-col bg-gradient-to-br from-[#011a4d] via-[#012E80] to-[#01245f] p-16 xl:p-24 text-white relative overflow-hidden'>
                {/* Mesh Glow Blobs */}
                <div className="absolute top-[-10%] right-[-10%] w-[60%] h-[60%] bg-white/10 rounded-full blur-[100px] animate-pulse-slow"></div>
                <div className="absolute bottom-[-10%] left-[-10%] w-[50%] h-[50%] bg-[#012E80]/30 rounded-full blur-[80px]"></div>

                {/* Logo top-left */}
                <div className={`mb-20 transition-all duration-1000 ${isLoaded ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
                    <img src={agoraCoreNew} alt='AGORA' className='h-24 w-auto object-contain opacity-90 brightness-110' />
                </div>

                <div className='flex-1 flex flex-col justify-center relative z-10'>
                    <h2 className={`text-4xl xl:text-5xl font-bold mb-16 tracking-tight transition-all duration-1000 delay-300 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                        Hi, Welcome back
                    </h2>

                    <div className='space-y-10'>
                        {[
                            { icon: IconUsersGroup, title: 'Comprehensive HR Management', desc: 'Streamline employee data, departments, and attendance tracking in one central hub.' },
                            { icon: IconClock, title: 'Leave & Time Tracking', desc: 'Effortlessly manage leave requests, balances, and detailed timesheets for your workforce.' },
                            { icon: IconChartBar, title: 'Performance & KPI Insights', desc: 'Monitor organizational growth with intuitive KPI dashboards and performance history tracking.' },
                            { icon: IconCreditCard, title: 'Financial & Accounts Control', desc: 'Stay on top of your bottom line with integrated management of payments, expenses, and invoices.' }
                        ].map((item, idx) => (
                            <div
                                key={idx}
                                className={`flex items-start gap-6 group transition-all duration-1000 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                                style={{ transitionDelay: `${100 + (idx * 150)}ms` }}
                            >
                                <div className='p-3.5 bg-white/10 rounded-2xl group-hover:bg-white/20 transition-all duration-300 shadow-lg shadow-black/5'>
                                    <item.icon size={26} className='text-white' />
                                </div>
                                <div className='pt-1'>
                                    <h3 className='text-lg xl:text-xl font-bold mb-1.5 group-hover:text-white transition-colors'>{item.title}</h3>
                                    <p className='text-white/70 text-sm leading-relaxed max-w-sm font-light'>
                                        {item.desc}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Right Side: Reset Form (60%) */}
            <div className='w-full lg:w-[60%] flex flex-col bg-white overflow-y-auto px-8 md:px-16 lg:px-24 py-12'>
                <div className={`flex-1 flex items-center justify-center py-20 transition-all duration-1000 delay-700 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                    <div className='w-full max-w-[460px]'>
                        <div className='text-center mb-14'>
                            <h1 className='text-4xl font-black text-gray-900 mb-3 tracking-tight'>Reset Password</h1>
                            <p className="text-gray-500 font-light">
                                {submitted
                                    ? 'Check your email for instructions'
                                    : 'Enter your email to receive reset instructions'}
                            </p>
                        </div>

                        {!submitted ? (
                            <form onSubmit={handleSubmit} className='flex flex-col gap-8'>
                                <div className='flex flex-col gap-2'>
                                    <label className='text-sm font-semibold text-gray-700 ml-1'>Email Address</label>
                                    <input
                                        type='email'
                                        placeholder='Enter your registered email'
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        disabled={loading}
                                        required
                                        className='w-full px-5 py-4 rounded-xl border border-gray-200 focus:border-[#012E81] focus:ring-2 focus:ring-[#012E81]/20 transition-all outline-none text-gray-800'
                                    />
                                </div>

                                <button
                                    type='submit'
                                    disabled={loading}
                                    className='mt-4 py-4 bg-[#012E80] hover:bg-[#011a4d] text-white text-lg font-bold rounded-xl transition-all duration-300 transform active:scale-[0.98] shadow-lg shadow-[#012E80]/20 flex justify-center items-center group'
                                >
                                    {loading ? (
                                        <div className='w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin'></div>
                                    ) : (
                                        'Send Reset Link'
                                    )}
                                </button>

                                <div className='text-center mt-6'>
                                    <Link to="/auth-login" className='inline-flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-[#012E80] transition-colors group'>
                                        <IconChevronLeft size={18} className='group-hover:-translate-x-1 transition-transform' />
                                        Back to Login
                                    </Link>
                                </div>
                            </form>
                        ) : (
                            <div className='text-center'>
                                <div className='mb-8 p-6 bg-[#6CBC50]/5 rounded-2xl border border-[#6CBC50]/10'>
                                    <p className='text-gray-600 leading-relaxed font-light'>
                                        We have sent a password reset link to <span className='font-semibold text-gray-900'>{email}</span>. Please check your inbox and follow the instructions.
                                    </p>
                                </div>
                                <Link to="/auth-login" className='inline-flex items-center gap-2 text-[#012E80] font-bold hover:underline group'>
                                    Back to sign in
                                    <IconChevronLeft size={18} className='rotate-180 group-hover:translate-x-1 transition-transform' />
                                </Link>
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer Visibility Improved */}
                <div className={`mt-auto pt-10 flex justify-center transition-all duration-1000 delay-1000 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}>
                    <div className='flex items-center gap-3 opacity-70 hover:opacity-100 transition-all duration-500 py-4 px-8 rounded-full border border-gray-200 hover:border-[#012E80]/20 hover:bg-[#012E80]/5 group cursor-default'>
                        <span className='text-[10px] uppercase tracking-[0.3em] font-medium text-gray-500'>Powered by</span>
                        <img src={hsclLogo2} alt='HSCL' className='h-6 w-auto object-contain brightness-95 group-hover:brightness-100' />
                    </div>
                </div>
            </div>

            <style jsx>{`
                @keyframes pulse-slow {
                    0%, 100% { opacity: 0.1; transform: scale(1); }
                    50% { opacity: 0.2; transform: scale(1.1); }
                }
                .animate-pulse-slow {
                    animation: pulse-slow 8s infinite ease-in-out;
                }
            `}</style>
        </div>
    )
}
